import { useState, useEffect } from 'react';
import { Clock, Flame } from 'lucide-react';

function getTimeLeft(endDate) {
  if (!endDate) return null;
  // Treat end_date as end of that day
  const end = new Date(endDate + 'T23:59:59');
  const now = new Date();
  const diff = end - now;
  if (diff <= 0) return { expired: true, total: 0 };
  return {
    expired: false,
    total: diff,
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };
}

/* ----- Compact badge shown on deal cards ----- */
export function DealTimerBadge({ endDate }) {
  const [tl, setTl] = useState(() => getTimeLeft(endDate));

  useEffect(() => {
    if (!endDate) return;
    setTl(getTimeLeft(endDate));
    const id = setInterval(() => setTl(getTimeLeft(endDate)), 60000); // update every minute
    return () => clearInterval(id);
  }, [endDate]);

  if (!tl || !endDate) return null;

  // Only show badge if ≤ 30 days left
  if (!tl.expired && tl.days > 30) return null;

  if (tl.expired) {
    return (
      <span className="flex items-center gap-1 text-xs font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-full">
        <Clock size={10} /> Expired
      </span>
    );
  }

  const isUrgent = tl.total < 24 * 3600 * 1000;          // < 24h
  const isClose  = tl.days <= 3;                           // ≤ 3 days
  const isWeek   = tl.days <= 7;                           // ≤ 7 days

  let label = '';
  if (isUrgent) {
    label = tl.hours > 0 ? `${tl.hours}h ${tl.minutes}m left` : `${tl.minutes}m left`;
  } else if (isClose) {
    label = `${tl.days}d ${tl.hours}h left`;
  } else {
    label = `${tl.days} days left`;
  }

  const cls = isUrgent
    ? 'bg-red-100 text-red-600'
    : isClose
    ? 'bg-orange-100 text-orange-600'
    : 'bg-[#F6F1E9] text-[#8A9A8B]';

  return (
    <span className={`flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${cls}`} data-testid="deal-timer-badge">
      {isUrgent || isClose ? <Flame size={10} /> : <Clock size={10} />}
      {label}
    </span>
  );
}

/* ----- Full countdown block shown on deal detail page ----- */
export function DealTimerFull({ endDate }) {
  const [tl, setTl] = useState(() => getTimeLeft(endDate));

  useEffect(() => {
    if (!endDate) return;
    setTl(getTimeLeft(endDate));
    const id = setInterval(() => setTl(getTimeLeft(endDate)), 1000);
    return () => clearInterval(id);
  }, [endDate]);

  if (!tl || !endDate) return null;

  const isUrgent = !tl.expired && tl.total < 24 * 3600 * 1000;
  const isClose  = !tl.expired && tl.days <= 3;

  const expireLabel = new Date(endDate + 'T23:59:59').toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });

  if (tl.expired) {
    return (
      <div className="rounded-2xl p-4 bg-red-50 border border-red-200" data-testid="deal-timer-full">
        <p className="text-sm font-bold text-red-500 flex items-center gap-2">
          <Clock size={14} /> This offer has expired
        </p>
      </div>
    );
  }

  const blocks = [
    { value: tl.days,    label: 'Days' },
    { value: tl.hours,   label: 'Hrs' },
    { value: tl.minutes, label: 'Min' },
    { value: tl.seconds, label: 'Sec' },
  ];

  const borderColor = isUrgent ? 'border-red-200' : isClose ? 'border-orange-200' : 'border-[#C8A97E]/30';
  const bgColor     = isUrgent ? 'bg-red-50'     : isClose ? 'bg-orange-50'      : 'bg-[#F6F1E9]';
  const labelColor  = isUrgent ? 'text-red-500'  : isClose ? 'text-orange-500'   : 'text-[#C8A97E]';
  const blockBg     = isUrgent ? 'bg-red-100'    : isClose ? 'bg-orange-100'     : 'bg-white';
  const blockText   = isUrgent ? 'text-red-600'  : isClose ? 'text-orange-600'   : 'text-[#2B2B2B]';

  return (
    <div className={`rounded-2xl p-4 border ${bgColor} ${borderColor}`} data-testid="deal-timer-full">
      <p className={`text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${labelColor}`}>
        {isUrgent || isClose ? <Flame size={12} /> : <Clock size={12} />}
        {isUrgent ? 'Ends Very Soon!' : isClose ? 'Ending Soon' : 'Offer Ends In'}
      </p>
      <div className="grid grid-cols-4 gap-2">
        {blocks.map(({ value, label }) => (
          <div key={label} className="text-center">
            <div className={`text-2xl font-bold rounded-xl py-2 ${blockBg} ${blockText}`}
              style={{ fontFamily: 'Playfair Display, serif' }}>
              {String(value).padStart(2, '0')}
            </div>
            <p className="text-xs text-[#2B2B2B]/50 mt-1">{label}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-center mt-3 text-[#2B2B2B]/50">
        Expires: {expireLabel}
      </p>
    </div>
  );
}
